import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, PencilSimple, Trash, Buildings, Sparkle, Warning } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const EMPTY = { name: "", upi_ids: [], mode_names: [] };

export default function PortalManagement() {
    const navigate = useNavigate();
    const [portals, setPortals] = useState([]);
    const [upis, setUpis] = useState([]);
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [upiSearch, setUpiSearch] = useState("");
    const [unlinked, setUnlinked] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [p, u, md, ul] = await Promise.all([
                api.get("/portals"), api.get("/settings/upis"), api.get("/payment-modes"),
                api.get("/payment-modes/unlinked-with-activity"),
            ]);
            setPortals(p.data || []);
            setUpis((u.data || []).map((x) => ({ upi_id: x.upi_id, label: x.label || x.name || "" })));
            setModes((md.data || []).map((x) => x.name).filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })));
            setUnlinked(ul.data?.modes || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditing(null); setForm(EMPTY); setUpiSearch(""); setDialogOpen(true); };
    const openEdit = (p) => { setEditing(p); setForm({ name: p.name || "", upi_ids: p.upi_ids || [], mode_names: p.mode_names || [] }); setUpiSearch(""); setDialogOpen(true); };

    const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

    // A withdrawal mode can only belong to one portal. Warn before poaching it.
    const portalForMode = (mn) => portals.find((p) => p.id !== (editing?.id) && (p.mode_names || []).some((x) => (x || "").toLowerCase() === mn.toLowerCase()));
    const toggleMode = (mn) => {
        if (!form.mode_names.includes(mn)) {
            const other = portalForMode(mn);
            if (other && !window.confirm(`This mode is already linked to "${other.name}". Reassign to this portal?`)) return;
        }
        toggle("mode_names", mn);
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Portal name is required");
        setSaving(true);
        try {
            if (editing) await api.patch(`/portals/${editing.id}`, form);
            else await api.post("/portals", form);
            toast.success(editing ? "Portal updated" : "Portal added");
            setDialogOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete portal "${p.name}"? Existing transactions are not changed.`)) return;
        try { await api.delete(`/portals/${p.id}`); toast.success("Portal deleted"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const autoMap = async () => {
        if (!window.confirm("Create/merge portals from your Company UPI 'Belongs to' labels?")) return;
        try {
            const { data } = await api.post("/portals/auto-map");
            toast.success(`Auto-map done: ${data.created} created · ${data.updated} updated`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="portals-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md">
                            <Buildings size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Portals</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Map each portal to the Company UPI accounts money is paid into and the payment modes used for withdrawals. Balances show on the Dashboard.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={autoMap} disabled={loading} data-testid="portal-auto-map"><Sparkle size={15} className="mr-1.5" /> Auto-map from UPI labels</Button>
                    <Button onClick={openAdd} data-testid="portal-add-btn"><Plus size={15} className="mr-1.5" /> Add Portal</Button>
                </div>
            </div>

            {unlinked.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="portal-unlinked-alert">
                    <div className="flex items-start gap-2">
                        <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
                        <div className="flex-1">
                            <div className="text-sm font-bold text-amber-900">{unlinked.length} withdrawal mode{unlinked.length === 1 ? "" : "s"} with transactions {unlinked.length === 1 ? "is" : "are"} not linked to any portal</div>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {unlinked.map((m) => (
                                    <span key={m.mode} data-testid={`portal-unlinked-${m.mode}`} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-300">{m.mode} · {m.count} txn{m.count === 1 ? "" : "s"} · ₹{Number(m.amount).toLocaleString("en-IN")}</span>
                                ))}
                            </div>
                            <Link to="/admin/settings/payment-modes" data-testid="portal-unlinked-link" className="mt-2 inline-block text-[11px] font-bold uppercase tracking-wider text-amber-700 underline hover:text-amber-900">Link them on the Payment Modes page →</Link>
                        </div>
                    </div>
                </div>
            )}

            {portals.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <EmptyState icon={Buildings} title="No portals yet" description="Add a portal (e.g. Goodluck) and map its UPI accounts + payment modes." />
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {portals.map((p) => (
                        <div key={p.id} data-testid={`portal-row-${p.id}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                                <div className="font-display text-lg font-bold text-slate-900">{p.name}</div>
                                <span data-testid={`portal-mode-count-${p.id}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">{(p.mode_names || []).length} modes</span>
                            </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => navigate(`/admin/portals/${p.id}/statement`)} data-testid={`portal-statement-${p.id}`} className="rounded px-1.5 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50">Statement →</button>
                                    <button onClick={() => openEdit(p)} data-testid={`portal-edit-${p.id}`} className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700"><PencilSimple size={15} /></button>
                                    <button onClick={() => remove(p)} data-testid={`portal-delete-${p.id}`} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash size={15} /></button>
                                </div>
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500">UPI accounts</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {(p.upi_ids || []).length ? p.upi_ids.map((u) => <span key={u} className="rounded bg-indigo-50 px-1.5 py-0.5 font-mono text-[10px] text-indigo-700">{u}</span>) : <span className="text-[11px] text-slate-400">None</span>}
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500">Linked Withdrawal Modes</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {(p.mode_names || []).length ? p.mode_names.map((m) => <span key={m} data-testid={`portal-tag-mode-${p.id}-${m}`} className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">{m}</span>) : <span className="text-[11px] text-slate-400">None</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="portal-dialog" className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Portal" : "Add Portal"}</DialogTitle>
                        <DialogDescription className="sr-only">Create or edit a portal and map its UPI accounts and payment modes.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Name *</Label>
                            <Input data-testid="portal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Goodluck" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Company UPI accounts (slips paid here add to this portal)</Label>
                            {upis.length > 0 && (
                                <Input
                                    data-testid="portal-upi-search"
                                    value={upiSearch}
                                    onChange={(e) => setUpiSearch(e.target.value)}
                                    placeholder="Search UPI ID..."
                                    className="mt-1.5 h-8 text-sm"
                                />
                            )}
                            <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                                {upis.length ? (() => {
                                    const q = upiSearch.trim().toLowerCase();
                                    const shown = q ? upis.filter((u) => (u.upi_id || "").toLowerCase().includes(q)) : upis;
                                    if (shown.length === 0) return <p data-testid="portal-upi-no-match" className="text-[11px] text-slate-400">No UPI ID found</p>;
                                    return shown.map((u) => (
                                        <label key={u.upi_id} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" data-testid={`portal-upi-${u.upi_id}`} checked={form.upi_ids.includes(u.upi_id)} onChange={() => toggle("upi_ids", u.upi_id)} />
                                            <span className="font-mono text-xs">{u.upi_id}</span>{u.label && <span className="text-[11px] text-slate-400">· {u.label}</span>}
                                        </label>
                                    ));
                                })() : <p className="text-[11px] text-slate-400">No Company UPIs. Add them under Settings → Company UPI IDs.</p>}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Linked Withdrawal Modes (money paid via these subtract from this portal)</Label>
                            <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                                {modes.length ? modes.map((mn) => {
                                    const other = portalForMode(mn);
                                    return (
                                        <label key={mn} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" data-testid={`portal-mode-${mn}`} checked={form.mode_names.includes(mn)} onChange={() => toggleMode(mn)} />
                                            <span>{mn}</span>
                                            {other && !form.mode_names.includes(mn) && (
                                                <span data-testid={`portal-mode-linked-${mn}`} className="rounded bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold text-rose-600">linked to {other.name}</span>
                                            )}
                                        </label>
                                    );
                                }) : <p className="text-[11px] text-slate-400">No payment modes yet.</p>}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="portal-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="portal-save">{saving ? "Saving…" : (editing ? "Update" : "Add")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
